import { Button } from "@bcc/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { cn } from "@bcc/lib/utils";
import type { Table } from "@tanstack/react-table";
import type { DataTableFilterGroup, DataTableRow } from "./data-table.types";
import { setFilterGroupOptionTestId, setFilterGroupTestId } from "./data-table.utils";

// Radix Select reserves the empty string, so "no filter" needs its own value.
const ALL_OPTION_VALUE = "__all__";

interface DataTableFilterGroupsProps<T extends object> {
  readonly table: Table<DataTableRow<T>>;
  readonly groups: DataTableFilterGroup[];
}

const toSelection = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.flatMap((item: unknown) => (typeof item === "string" ? [item] : []));
  return typeof value === "string" ? [value] : [];
};

const readSelection = <T extends object>(table: Table<DataTableRow<T>>, group: DataTableFilterGroup): string[] => {
  if (group.selected) return group.selected;
  return group.column ? toSelection(table.getColumn(group.column)?.getFilterValue()) : [];
};

const writeSelection = <T extends object>(table: Table<DataTableRow<T>>, group: DataTableFilterGroup, next: string[]) => {
  group.onSelectedChange?.(next);
  if (group.selected === undefined && group.column) {
    table.getColumn(group.column)?.setFilterValue(next.length > 0 ? next : undefined);
  }
};

interface GroupProps<T extends object> {
  readonly table: Table<DataTableRow<T>>;
  readonly group: DataTableFilterGroup;
}

function FilterGroupButtons<T extends object>({ table, group }: GroupProps<T>) {
  const selected = readSelection(table, group);
  const toggle = (value: string) =>
    writeSelection(table, group, selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);

  return (
    <>
      {group.options.map((option) => {
        const isActive = selected.includes(option.value);
        return (
          <Button
            key={option.value}
            testId={setFilterGroupOptionTestId(group.id, option.value)}
            variant={isActive ? "default" : "outline"}
            size="sm"
            aria-pressed={isActive}
            onClick={() => toggle(option.value)}
            className={cn("h-8 rounded-full", !isActive && option.className)}
          >
            {option.label}
          </Button>
        );
      })}
    </>
  );
}

function FilterGroupDropdown<T extends object>({ table, group }: GroupProps<T>) {
  const value = readSelection(table, group)[0] ?? ALL_OPTION_VALUE;
  const handleChange = (next: string) => writeSelection(table, group, next === ALL_OPTION_VALUE ? [] : [next]);

  return (
    <Select value={value} onValueChange={handleChange}>
      <SelectTrigger
        className="h-8 min-w-40"
        aria-label={group.label ?? group.allOptionLabel}
        testId={setFilterGroupOptionTestId(group.id, "trigger")}
      >
        <SelectValue placeholder={group.allOptionLabel} />
      </SelectTrigger>
      <SelectContent>
        {group.allOptionLabel ? (
          <SelectItem value={ALL_OPTION_VALUE} testId={setFilterGroupOptionTestId(group.id, ALL_OPTION_VALUE)}>
            {group.allOptionLabel}
          </SelectItem>
        ) : null}
        {group.options.map((option) => (
          <SelectItem key={option.value} value={option.value} testId={setFilterGroupOptionTestId(group.id, option.value)}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Labelled filter groups for the DataTable filter bar. Each group renders as
 * toggle buttons or as a single-choice dropdown, see {@link DataTableFilterGroup}.
 */
export function DataTableFilterGroups<T extends object>({ table, groups }: DataTableFilterGroupsProps<T>) {
  return (
    <>
      {groups.map((group) => (
        <fieldset
          key={group.id}
          className="m-0 flex min-w-0 flex-wrap items-center gap-2 border-0 p-0"
          data-testid={setFilterGroupTestId(group.id)}
        >
          {group.label ? <legend className="float-left text-sm text-muted-foreground">{group.label}</legend> : null}
          {group.display === "dropdown" ? (
            <FilterGroupDropdown table={table} group={group} />
          ) : (
            <FilterGroupButtons table={table} group={group} />
          )}
        </fieldset>
      ))}
    </>
  );
}
